/**
 * TRAILS FROM HERE — where a reader can go next, and why.
 *
 * The panel could say everything about a figure and still leave "so who is this connected to?"
 * answerable only by closing it and hunting the map for a line. Theirs answers it in a row of chips
 * at the foot of the panel (engine.js 2258, `fNeighbours`), and it is the piece that turns a panel
 * into a way of travelling rather than a terminus.
 *
 * WHAT EACH CHIP CARRIES, all of it theirs:
 *
 *   - the neighbour's name and its scripture colour, so the chip reads like the circle on the map;
 *   - `inward` or `outward` — whether it sits on a ring nearer the centre or further out, which is
 *     the one thing a reader cannot tell from a name;
 *   - the citation the tie was drawn from, where the build recorded one. A trail that can say
 *     "linked by Genesis 5:18" is a different offer from one that cannot, and theirs sorts on
 *     exactly that: cited ties come first, so the seven shown are the seven that can explain
 *     themselves.
 *
 * Every dimension is an argument. The cap of seven, which ties are too weak to offer, and how a ring
 * is compared are all passed in — the cap because it is a screenful and a screenful is ours to
 * decide, the rest because our map is not laid out on their radii.
 *
 * NOT PORTED: their frontier. Theirs dims a neighbour beyond the reader's reach, draws it dashed and
 * refuses to travel there. Whether this map gates travel that way is a question standing open with
 * Ariel — see V8-PORT.md — so this builds the list and says nothing about reach, and the answer,
 * whichever way it goes, is a field on `Trail` rather than a rewrite.
 */

/** What a trail needs to know about the figure at the other end. */
export interface TrailTarget {
  readonly id: string;
  readonly name: string;
  /** The scripture colour the map draws it in — the chip wears the same one. */
  readonly colour: string;
  /**
   * Which ring it sits on. Compared, never displayed: only nearer/further reaches the reader.
   * Smaller is nearer the centre, matching the map's own radii.
   */
  readonly ring: number;
  /** Beings take a round mark and places a square one, as on the map. */
  readonly glyph: 'round' | 'square';
}

export interface Trail extends TrailTarget {
  /** '' where the tie stays on the same ring, which is most of them. */
  readonly direction: 'inward' | 'outward' | '';
  /** The passage the tie was drawn from; null where the build recorded none. */
  readonly ref: string | null;
}

/*
 * WHAT THEIRS SAYS ON A CHIP THAT OURS DOES NOT, and why it is not simply added — compared
 * 2026-08-22 against `engine.js:2268` and `universe-v8.dc.html:440`.
 *
 * Every other signal matches. Their glyph is `o.kind==='l' ? '2px' : '50%'` — square for a place and
 * round for everything else, which is exactly our two-shape `glyph` and not the map's three; their
 * `dir` is inward/outward/'' by comparing rungs, which is ours; the colour is the scripture's on
 * both; and the "Linked by <ref>" title is ours too.
 *
 * What theirs adds is REACHABILITY, four ways at once:
 *
 *     dim:    reach ? 1 : 0.55
 *     dash:   reach ? 'solid' : 'dashed'
 *     border: fainter when reachable, stronger when not
 *     title:  'Beyond your frontier' where there is no citation to name
 *     go:     `warp` when reachable, `look` when not — the camera turns and nothing opens
 *
 * WE HAVE THE FRONTIER ALREADY. `App.tsx` derives `fogWithin` from the ties on every render and the
 * map's fog draws from it; it simply is not handed to these chips.
 *
 * AND IT IS NOT GOING TO BE. Ariel decided on 2026-08-22: *"drop the feature that prevents you from
 * going to a node"*. Every one of those five signals exists to mark a place the reader may not go,
 * and on this map there is no such place — so a dashed, dimmed chip reading "beyond your frontier"
 * would state a limit the app does not keep, and a reader would learn a rule that is not true.
 *
 * The chips carry what is true instead: which scripture colours a name, whether it is a place or a
 * being, which way its rung lies, and the passage the tie was drawn from. Nothing here is missing.
 *
 * The frontier is not lost with the gate — the map's fog still shows how far a reader has been. It
 * is the difference between showing someone where they have walked and stopping them walking
 * further, and only the second was dropped.
 *
 * AND IT WAS TWO SURFACES, NOT ONE. Theirs tags every SEARCH RESULT the same way — its list for
 * "Jo" reads `Joseph / Yusuf · Person · 3 corpora · beyond reach`, and every row carries that last
 * part. Ours matches on all the rest: the paired other name, the kind, and the corpora written out
 * when there are few (`Person · Hebrew Bible & New Testament`) or counted when there are many.
 *
 * One decision covered both, which is why neither was given it alone — a reader meeting "beyond
 * reach" in the search and not on the chips would have learned the wrong rule twice over. Both stay
 * as they are; see `search/searchNodes.ts`, which carries the same note.
 */

/** engine.js:2268 — `out.slice(0,7)`. Seven chips is two rows at the panel's width. */
export const TRAIL_CAP = 7;

/**
 * The trails from one figure, in the order they should be offered.
 *
 * `ties` may repeat an id and may name the figure itself; both are dropped, as theirs does with its
 * `seen` set. `refFor` answers the citation for an unordered pair, and `weak` refuses a tie
 * altogether — an edge the map draws faintly is not an invitation.
 */
export function trailsFrom(
  from: TrailTarget,
  ties: Iterable<string>,
  lookup: (id: string) => TrailTarget | undefined,
  refFor: (a: string, b: string) => string | null,
  { cap = TRAIL_CAP, weak = (): boolean => false }: {
    cap?: number;
    weak?: (a: string, b: string) => boolean;
  } = {},
): Trail[] {
  const seen = new Set<string>([from.id]);
  const out: Trail[] = [];
  for (const id of ties) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (weak(from.id, id)) continue;
    const target = lookup(id);
    if (target === undefined) continue;
    out.push({
      ...target,
      direction: target.ring < from.ring ? 'inward' : target.ring > from.ring ? 'outward' : '',
      ref: refFor(from.id, id),
    });
  }
  /*
   * Cited ties first, and STABLY: within each group the map's own order survives, so the same figure
   * offers the same trails in the same places from one visit to the next. A comparator that ranked
   * anything further would make the row reshuffle for reasons no reader could see.
   */
  out.sort((a, b) => (b.ref === null ? 0 : 1) - (a.ref === null ? 0 : 1));
  return out.slice(0, cap);
}
